<template>
    <div class="login-containers">
        <div class="login-header">
            <div class="nav">
                <a href="http://www.tgr.co/" target="_blank"><Logo class="logo" /></a>
            </div>
        </div>
        <div class="login-main">
            <div class="about">
                <h1>Our Autonomous Deployment Information System (ADIS) enables ADIBOT operators to track disinfection cycles with detailed logs and real-time data, ensuring compliance for efficient monitoring and reporting.</h1>
            </div>
            <div class="feature">
                <div class="product">ADIS</div>
                <div class="forms">
                    <div class="methods">
                        <span :class="loginMethod == 'PASSWORD' ? 'on' : ''" @click="changeLoginType('PASSWORD')">{{ $t('user.loginPassword') }}</span>
                        <span :class="loginMethod == 'EMAIL' ? 'on' : ''" @click="changeLoginType('EMAIL')">{{ $t('user.loginEmail') }}</span>
                    </div>
                    <el-form v-if="loginMethod == 'PASSWORD'" ref="loginFormRef" :model="loginForm" :rules="loginRules" class="login-form" autocomplete="on" label-position="left">
                        <el-form-item prop="company_id" class="loginFormItem">
                            <el-input ref="userNameRef" v-model="loginForm.company_id" name="company_id" :placeholder="$t('login.company_id')" type="text" tabindex="1" autocomplete="on"></el-input>
                        </el-form-item>
                        <el-form-item prop="username" class="loginFormItem">
                            <el-input v-model="loginForm.username" name="username" :placeholder="$t('user.name')" type="text" tabindex="1" autocomplete="on"></el-input>
                        </el-form-item>
                        <el-tooltip v-model="capsTooltip" content="Caps lock is On" placement="right" manual>
                            <el-form-item prop="password" class="loginFormItem">
                                <el-input v-model="loginForm.password" ref="passwordRef" name="password" type="password" :placeholder="$t('login.password')" show-password @keyup="checkCapslock" @blur="capsTooltip = false" tabindex="2" autocomplete="on" @keyup.enter="handleLogin"></el-input>
                            </el-form-item>
                        </el-tooltip>
                        <el-form-item>
                            <el-button :loading="loading" class="login-btn" @click.prevent="handleLogin" @keyup.space.prevent.native @keydown.enter.prevent.native>
                                {{ $t('login.logIn') }}
                            </el-button>
                            <FindKey />
                        </el-form-item>
                    </el-form>
                    <el-form v-else ref="loginEmailFormRef" :model="loginEmailForm" :rules="loginEmailRules" class="login-form" autocomplete="on" label-position="left">
                        <div v-if="loginEmailStep == 'STEP1'" class="step1">
                            <el-form-item prop="company_id" class="loginFormItem">
                                <el-input ref="userNameRef" v-model="loginEmailForm.company_id" :placeholder="$t('login.company_id')" name="company_id" type="text" tabindex="1" autocomplete="on" />
                            </el-form-item>
                            <el-form-item prop="email" class="loginFormItem">
                                <el-input v-model="loginEmailForm.email" :placeholder="$t('user.email')" name="email" type="text" tabindex="1" autocomplete="on" />
                            </el-form-item>
                            <el-tooltip v-model="capsTooltip" content="Caps lock is On" placement="right" manual>
                                <el-form-item prop="password" class="loginFormItem">
                                    <el-input ref="passwordEmailRef" v-model="loginEmailForm.password" type="password" :placeholder="$t('login.password')" name="password" show-password tabindex="2" autocomplete="on" @keyup="checkCapslock" @blur="capsTooltip = false" @keyup.enter="handleEmailLogin" />
                                </el-form-item>
                            </el-tooltip>
                            <el-form-item>
                                <el-button :loading="loading" class="login-btn" @click.prevent="handleEmailLogin" @keyup.space.prevent.native @keydown.enter.prevent.native>
                                    {{ $t('login.logIn') }}
                                </el-button>
                                <FindKey />
                            </el-form-item>
                        </div>
                        <div v-else class="step2">
                            <p>{{ $t('user.emailLogintip1') }}</p>
                            <p>
                                <span v-html="countdownTips"></span>
                                <el-link :type="leftTime == 0 ? 'primary' : 'default'" :disabled="leftTime != 0" @click.prevent="reSendEmail">【{{ $t('user.resend') }}】</el-link>
                            </p>
                            <el-form-item class="loginFormItem">
                                <el-input v-model="emailVerifyCode" name="emailVerifyCode" type="text" />
                            </el-form-item>
                            <el-checkbox v-model="isRemember" :label="$t('user.noverifyday30')" />
                            <div style="clear:both; padding-top:30px; text-align: center;">
                                <el-button class="login-btn" style="width:70%;" :type="emailVerifyCode == '' ? 'info' : 'primary'" :disabled="emailVerifyCode == ''" @click="verifyEmail">
                                    {{ $t('user.loginConfirm') }}
                                </el-button>
                            </div>
                        </div>
                    </el-form>
                </div>
            </div>
            <div class="footer">
                <div class="empty"></div>
                <div class="agreement">
                    {{ $t('login.loginAgree') }}
                    <a href="http://www.tgr.co/terms-of-use" data-w-id="755c0c82-9f46-c506-a0c9-0ef1ea15421f" target="_blank">{{ $t('login.termsService') }}</a>
                    {{ $t('login.and') }}
                    <a href="https://tgr.co/privacy-policy" target="_blank">{{ $t('login.privacyAgreement') }}</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, watch, ref, nextTick, toRefs } from 'vue';
import LangSelect from '@/components/lang_select/Index.vue';
import FindKey from '@/components/find-key/Index.vue';
import { isValidUsername } from '@/utils/validate';
import { useRoute, LocationQuery, useRouter } from 'vue-router';
import { useStore } from '@/store';
import { UserActionTypes } from '@/store/modules/user/action-types';
import Agreement from './Agreement.vue';
import i18n from '@/locales/index';
import { getRemember, setRemember } from '@/utils/cookies';
import { ElMessage } from 'element-plus';
import https from '@/utils/https';
import { RootObject } from '@/model/rootObject';
import { LoginModel } from './model/loginModel';
import { ContentType, Method } from 'axios-mapper';
import Logo from './logo.vue';
export default defineComponent({
    components: {
        LangSelect,
        Agreement,
        FindKey,
        Logo
    },
    setup() {
        const userNameRef = ref(null);
        const passwordRef = ref(null);
        const loginFormRef = ref(null);
        const router = useRouter();
        const route = useRoute();
        const store = useStore();

        //
        const loginMethod = ref<string>('PASSWORD');
        const loginEmailFormRef = ref(null);
        const validateEmail = (rule, value, callback) => {
            if (value === '') callback(new Error(i18n.global.t('email.placeHolder')));
            else if (!/^[a-zA-Z0-9\u4e00-\u9fa5\u00C0-\u024F](([_-]|\.)?[a-zA-Z0-9\u4e00-\u9fa5\u00C0-\u024F]+)+@[a-zA-Z0-9]+([_-]?[a-zA-Z0-9]+)?(\.[a-zA-Z0-9]+([_-]?[a-zA-Z0-9]+)?)+$/.test(value)) callback(new Error(i18n.global.t('user.emailRule2')));
            else callback();
        };
        // 邮箱登录步骤1【STEP1】 输入信息进行验证
        // 邮箱登录步骤2【STEP2】 需要输入验证码验证
        const loginEmailStep = ref('STEP1');
        const emailVerifyCode = ref('');
        const isRemember = ref(false);
        const countdownTips = ref('');
        let timer = 0;
        const leftTime = ref(0);
        //
        // const { t } = useI18n()
        const state = reactive({
            loginForm: {
                username: '',
                password: '',
                company_id: ''
            },
            loginRules: {
                company_id: [
                    {
                        required: true,
                        message: i18n.global.t('login.tip_company'),
                        trigger: 'blur'
                    }
                ],
                username: [
                    {
                        required: true,
                        message: i18n.global.t('user.placeHolder'),
                        trigger: 'blur'
                    }
                ],
                password: [
                    {
                        required: true,
                        message: i18n.global.t('login.tip_pwd'),
                        trigger: 'blur'
                    }
                ]
            },
            passwordType: 'password',
            loading: false,
            showDialog: false,
            capsTooltip: false,
            redirect: '',
            otherQuery: {},
            loginEmailForm: {
                email: '',
                password: '',
                company_id: '',
                need: false,
                code: '',
                remember: false
            },
            loginEmailRules: {
                company_id: [
                    {
                        required: true,
                        message: i18n.global.t('login.tip_company'),
                        trigger: 'blur'
                    }
                ],
                email: [{ required: true, validator: validateEmail, trigger: 'blur' }],
                password: [
                    {
                        required: true,
                        message: i18n.global.t('login.tip_pwd'),
                        trigger: 'blur'
                    }
                ]
            }
        });

        const methods = reactive({
            changeLoginType: (type: string) => {
                state.loginEmailForm = {
                    email: '',
                    password: '',
                    company_id: '',
                    need: false,
                    code: '',
                    remember: false
                };
                state.loginForm = {
                    username: '',
                    password: '',
                    company_id: ''
                };
                loginMethod.value = type;
            },
            validateUsername: (rule: any, value: string, callback: Function) => {
                if (!isValidUsername(value)) {
                    callback(new Error('Please enter the correct user name'));
                } else {
                    callback();
                }
            },
            validatePassword: (rule: any, value: string, callback: Function) => {
                if (value.length < 6) {
                    callback(new Error('The password can not be less than 6 digits'));
                } else {
                    callback();
                }
            },
            checkCapslock: (e: KeyboardEvent) => {
                const { key } = e;
                state.capsTooltip = key !== null && key.length === 1 && key >= 'A' && key <= 'Z';
            },
            showPwd: () => {
                if (state.passwordType === 'password') {
                    state.passwordType = '';
                } else {
                    state.passwordType = 'password';
                }
                nextTick(() => {
                    (passwordRef.value as any).focus();
                });
            },
            handleLogin: () => {
                (loginFormRef.value as any).validate(async (valid: boolean) => {
                    if (valid) {
                        state.loading = true;
                        await store.dispatch(UserActionTypes.ACTION_LOGIN, state.loginForm);
                        router
                            .push({
                                path: state.redirect || '/',
                                query: state.otherQuery
                            })
                            .catch((err) => {
                                console.warn(err);
                            });
                        // Just to simulate the time of the request
                        setTimeout(() => {
                            state.loading = false;
                        }, 0.5 * 1000);
                    } else {
                        return false;
                    }
                });
            },
            handleEmailLogin: () => {
                (loginEmailFormRef.value as any).validate(async (valid: boolean) => {
                    if (!valid) return false;
                    https(false)
                        .request<RootObject<LoginModel>>(
                            'email_login',
                            Method.POST,
                            {
                                email: state.loginEmailForm.email,
                                company_id: state.loginEmailForm.company_id,
                                password: state.loginEmailForm.password,
                                need: getRemember(state.loginEmailForm.company_id, state.loginEmailForm.email) ? false : true,
                                code: ''
                            },
                            ContentType.json
                        )
                        .then(async (res) => {
                            switch (res?.code) {
                                case 200:
                                    if (res.data && res.data.token) {
                                        console.log('login success');
                                        await store.dispatch(UserActionTypes.ACTION_LOGIN_EMAIL, {
                                            company_id: state.loginEmailForm.company_id,
                                            email: state.loginEmailForm.email,
                                            remember: false,
                                            token: res.data.token
                                        });
                                        router
                                            .push({
                                                path: state.redirect || '/',
                                                query: state.otherQuery
                                            })
                                            .catch((err) => {
                                                console.log(err);
                                            });
                                    } else {
                                        loginEmailStep.value = 'STEP2';
                                        methods.countDown();
                                    }
                                    break;
                                case 400:
                                    ElMessage({
                                        message: i18n.global.t('global.error400'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 30002:
                                    ElMessage({
                                        message: i18n.global.t('global.error30002'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 40001:
                                    ElMessage({
                                        message: i18n.global.t('system.companyNoExis'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 40002:
                                    ElMessage({
                                        message: i18n.global.t('system.userNoExis'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 40003:
                                    ElMessage({
                                        message: i18n.global.t('global.error40003'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 400031:
                                    ElMessage({
                                        type: 'error',
                                        duration: 1e3 * 5,
                                        message: i18n.global.t('system.passwordNoExis').replace('N', '1')
                                    });
                                    break;
                                case 400032:
                                    ElMessage({
                                        type: 'error',
                                        duration: 1e3 * 5,
                                        message: i18n.global.t('system.passwordNoExis').replace('N', '2')
                                    });
                                    break;
                                case 400033:
                                    ElMessage({
                                        type: 'error',
                                        duration: 1e3 * 5,
                                        message: i18n.global.t('system.passwordNoExis').replace('N', '3')
                                    });
                                    break;
                                case 400034:
                                    ElMessage({
                                        type: 'error',
                                        duration: 1e3 * 5,
                                        message: i18n.global.t('system.passwordNoExis').replace('N', '4')
                                    });
                                    break;
                                case 400035:
                                    ElMessage({
                                        type: 'error',
                                        duration: 1e3 * 5,
                                        message: i18n.global.t('system.passwordNoExis').replace('N', '5')
                                    });
                                    break;
                                case 40007:
                                    ElMessage({
                                        message: i18n.global.t('user.DisableUser'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 40008:
                                    ElMessage({
                                        message: i18n.global.t('global.error40008'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                case 40009:
                                    ElMessage({
                                        message: i18n.global.t('global.error40009'),
                                        type: 'error',
                                        duration: 1e3 * 5
                                    });
                                    break;
                                default:
                                    console.log('===============UNKNOWN==================');
                                    break;
                            }
                        })
                        .catch((err) => {
                            console.warn(err);
                        });
                });
            },
            verifyEmail: async () => {
                https(false)
                    .request<RootObject<LoginModel>>(
                        'email_login',
                        Method.POST,
                        {
                            email: state.loginEmailForm.email,
                            company_id: state.loginEmailForm.company_id,
                            password: state.loginEmailForm.password,
                            need: true,
                            code: emailVerifyCode.value
                        },
                        ContentType.json
                    )
                    .then(async (res) => {
                        switch (res?.code) {
                            case 200:
                                if (res.data && res.data.token) {
                                    console.log('login success');
                                    await store.dispatch(UserActionTypes.ACTION_LOGIN_EMAIL, {
                                        company_id: state.loginEmailForm.company_id,
                                        email: state.loginEmailForm.email,
                                        remember: isRemember.value,
                                        token: res.data.token
                                    });
                                    router
                                        .push({
                                            path: state.redirect || '/',
                                            query: state.otherQuery
                                        })
                                        .catch((err) => {
                                            console.log(err);
                                        });
                                } else {
                                    console.log(res);
                                }
                                break;
                            case 400:
                                ElMessage({
                                    message: i18n.global.t('global.error400'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 30002:
                                ElMessage({
                                    message: i18n.global.t('global.error30002'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40001:
                                ElMessage({
                                    message: i18n.global.t('system.companyNoExis'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40002:
                                ElMessage({
                                    message: i18n.global.t('system.userNoExis'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40003:
                                ElMessage({
                                    message: i18n.global.t('global.error40003'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40007:
                                ElMessage({
                                    message: i18n.global.t('user.DisableUser'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40008:
                                ElMessage({
                                    message: i18n.global.t('global.error40008'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40009:
                                ElMessage({
                                    message: i18n.global.t('global.error40009'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            default:
                                console.log('===============UNKNOWN==================');
                                break;
                        }
                    })
                    .catch((err) => {
                        console.warn(err);
                    });
            },
            reSendEmail: async () => {
                https(false)
                    .request<RootObject<LoginModel>>(
                        'email_login',
                        Method.POST,
                        {
                            email: state.loginEmailForm.email,
                            company_id: state.loginEmailForm.company_id,
                            password: state.loginEmailForm.password,
                            need: true,
                            code: ''
                        },
                        ContentType.json
                    )
                    .then(async (res) => {
                        switch (res?.code) {
                            case 200:
                                loginEmailStep.value = 'STEP2';
                                ElMessage({
                                    message: i18n.global.t('user.tipsresendemail'),
                                    type: 'success',
                                    duration: 1e3 * 5
                                });
                                methods.countDown();
                                break;
                            case 400:
                                ElMessage({
                                    message: i18n.global.t('global.error400'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 30002:
                                ElMessage({
                                    message: i18n.global.t('global.error30002'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40001:
                                ElMessage({
                                    message: i18n.global.t('system.companyNoExis'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40002:
                                ElMessage({
                                    message: i18n.global.t('system.userNoExis'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40003:
                                ElMessage({
                                    message: i18n.global.t('global.error40003'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40007:
                                ElMessage({
                                    message: i18n.global.t('user.DisableUser'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40008:
                                ElMessage({
                                    message: i18n.global.t('global.error40008'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            case 40009:
                                ElMessage({
                                    message: i18n.global.t('global.error40009'),
                                    type: 'error',
                                    duration: 1e3 * 5
                                });
                                break;
                            default:
                                console.log('===============UNKNOWN==================');
                                break;
                        }
                    })
                    .catch((err) => {
                        console.warn(err);
                    });
            },
            countDown() {
                clearInterval(timer);
                leftTime.value = 30;
                countdownTips.value = i18n.global.t('user.emailLogintip2').replace('%d', `<span class="cd">${leftTime.value}</span>`);
                timer = window.setInterval(() => {
                    leftTime.value = leftTime.value - 1;
                    countdownTips.value = i18n.global.t('user.emailLogintip2').replace('%d', `<span class="cd">${leftTime.value}</span>`);
                    if (leftTime.value < 1) {
                        clearInterval(timer);
                    }
                }, 1e3);
            }
        });

        function getOtherQuery(query: LocationQuery) {
            return Object.keys(query).reduce((acc, cur) => {
                if (cur !== 'redirect') {
                    acc[cur] = query[cur];
                }
                return acc;
            }, {} as LocationQuery);
        }

        watch(
            () => route.query,
            (query) => {
                if (query) {
                    state.redirect = query.redirect?.toString() ?? '';
                    state.otherQuery = getOtherQuery(query);
                }
            }
        );
        return {
            userNameRef,
            passwordRef,
            loginFormRef,
            ...toRefs(state),
            ...toRefs(methods),
            // t
            loginMethod,
            loginEmailFormRef,
            loginEmailStep,
            emailVerifyCode,
            isRemember,
            leftTime,
            countdownTips
        };
    }
});
</script>

<style lang="scss" scoped>
*,
*::before,
*::after {
    box-sizing: border-box;
}
@import url('https://fonts.googleapis.com/css?family=Inter:100,200,300,regular,500,600,700,800,900,100italic,200italic,300italic,italic,500italic,600italic,700italic,800italic,900italic%7CIBM+Plex+Mono:regular');
.login-containers {
    width: 100%;
    height: 100%;
    max-width: 1440px;
    margin: 0 auto;
    font-family: 'Inter', sans-serif;
    .login-header {
        position: fixed;
        left: 0;
        top: 0;
        width: 100%;
        background-color: #000;
        padding: 1rem 1.5rem;
        line-height: 1.5;
        backdrop-filter: blur(48px);
        .nav {
            width: 100%;
            max-width: 1440px;
            margin: 0 auto;
            .logo {
                width: 100px;
                max-width: 100%;
                vertical-align: middle;
                height: 42%;
                display: inline-block;
            }
        }
    }
    .login-main {
        width: 100%;
        height: 100%;
        .about {
            width: 100%;
            padding: 10rem 0 3rem 0;
            h1 {
                width: 50%;
                line-height: 130%;
                letter-spacing: -0.5px;
                font-weight: 400;
                font-size: 2rem;
                margin: 0;
            }
        }
        .feature {
            display: flex;
            width: 100%;
            justify-content: flex-start;
            align-items: flex-end;
            grid-column-gap: 5rem;
            grid-row-gap: 5rem;
            flex-flow: row;
            margin-top: 3rem;
            margin-bottom: 3rem;
            .product {
                width: 50%;
                text-align: right;
                align-items: flex-end;
                font-size: 150px;
                font-weight: 100;
                letter-spacing: 40px;
                padding-bottom: 1.5rem;
                line-height: 100%;
            }
            .forms {
                width: 50%;
                grid-column-gap: 1.5rem;
                grid-row-gap: 1.5rem;
                .methods {
                    display: inline-block;
                    background-color: #000;
                    border-radius: 6px;
                    padding: 3px;
                    color: #fff;
                    font-size: 14px;
                    margin-bottom: 25px;

                    span {
                        display: inline-block;
                        cursor: pointer;
                        padding: 5px 24px;
                        transition: color 0.2s, background-color 0.2s;
                    }
                    span.on {
                        background-color: #fff;
                        color: #000;
                        border-radius: 3px;
                    }
                }
            }
        }
        .footer {
            display: flex;
            width: 100%;
            justify-content: flex-start;
            align-items: flex-end;
            grid-column-gap: 5rem;
            grid-row-gap: 5rem;
            flex-flow: row;
            padding: 0 1.5rem 3rem 0;
            .empty {
                width: 50%;
            }
            .agreement {
                width: 50%;
                font-size: 14px;
                text-align: right;
                align-items: flex-end;
                line-height: 1.5;
                a {
                    color: rgba(0, 0, 0, 0.5);
                    text-decoration: none;
                    &:hover {
                        color: rgba(0, 0, 0, 1);
                    }
                }
            }
        }
    }
}

.login-btn {
    display: inline-block;
    background-color: #000;
    color: #fff;
    letter-spacing: 3px;
    grid-column-gap: 1rem;
    grid-row-gap: 1rem;
    backdrop-filter: blur(52px);
    border: 1px solid #0000;
    border-radius: 6px !important;
    justify-content: center;
    align-items: center;
    height: 42px;
    padding-left: 24px !important;
    padding-right: 24px !important;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.5s linear;
    margin-bottom: 10px;
    &:hover {
        color: #000;
        background-color: #fff;
        border-color: #ccc;
    }
    &.is-disabled {
        color: #7d7d7d;
        background-color: #ccc;
    }
}

@media screen and (max-width: 991px) {
    .login-containers .login-main .about h1 {
        width: 100%;
        font-size: 24px;
    }
}
@media screen and (max-width: 767px) {
    .login-containers .login-main .feature {
        flex-flow: column;
    }
    .login-containers .login-main .feature .product {
        width: 100%;
    }
    .login-containers .login-main .feature .forms {
        width: 100%;
    }
    .login-containers .login-main .feature .forms .methods {
        width: 100%;
        display: inline-flex;
    }
    .login-containers .login-main .feature .forms .methods span {
        display: inline-flex;
        width: 50%;
        justify-content: center;
    }

    .login-containers .login-main .footer {
        flex-flow: column;
    }
    .login-containers .login-main .footer .empty {
        width: 100%;
    }
    .login-containers .login-main .footer .agreement {
        width: 100%;
    }
}
@media screen and (max-width: 479px) {
    .login-containers .login-main .feature .product {
        font-size: 90px;
    }
    .login-containers .login-main .feature .forms .methods span {
        padding: 1rem;
    }
}

::v-deep(.el-input .el-input__inner) {
    border: solid 1px #0000002b;
    height: 50px;
    padding: 15px 18px;
    font-size: 1rem;
    border-radius: 6px !important;
    background-color: #fff;
    color: #000;
}
::v-deep(.el-input__inner::placeholder) {
    color: #000 !important;
}
::v-deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
    background-color: #000;
    border-color: #000;
}
::v-deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
    color: #000;
}
::v-deep(.el-checkbox__inner:hover) {
    border-color: #000;
}
::v-deep(.el-checkbox__input.is-focus .el-checkbox__inner) {
    border-color: #000;
}
</style>
